import { useCallback, useEffect, useMemo, useState } from "react";
import { ANALYTICS_EVENTS, trackEvent } from "utils/analytics";
import { findRoomAtPoint } from "../../utils/rooms";
import { ACTIVE_PAGE_INDEX } from "../useSelectedRoom/constants";
import type {
  FloorPlanLayoutRoom,
  SelectionAreaPoint,
} from "../../utils/types";
import type { UseSelectedZone } from "./types";

const isOnActivePage = (row: { pageIndex: number }) =>
  row.pageIndex === ACTIVE_PAGE_INDEX;

export const useSelectedZone: UseSelectedZone = ({
  layout,
  isEnabled,
  resetKey,
  floorPlanPanelHandlers,
  onEnterRoom,
}) => {
  const [selectedZoneId, setSelectedZoneId] = useState<number | null>(null);

  const roomsByZoneId = useMemo(() => {
    const roomsByZone = new Map<number, FloorPlanLayoutRoom[]>();
    for (const room of layout.rooms) {
      if (!isOnActivePage(room) || room.zoneId === null) continue;
      const zoneRooms = roomsByZone.get(room.zoneId);
      if (zoneRooms) zoneRooms.push(room);
      else roomsByZone.set(room.zoneId, [room]);
    }
    return roomsByZone;
  }, [layout.rooms]);

  const selectedZone = useMemo(
    () =>
      layout.zones.find(
        (zone) => zone.id === selectedZoneId && isOnActivePage(zone),
      ) ?? null,
    [layout.zones, selectedZoneId],
  );

  const selectedZoneRoomIds = useMemo(() => {
    const zoneRooms = selectedZone && roomsByZoneId.get(selectedZone.id);
    return new Set(zoneRooms?.map((room) => room.id));
  }, [roomsByZoneId, selectedZone]);

  const selectZone = useCallback(
    (zoneId: number) => {
      const zoneRooms = roomsByZoneId.get(zoneId);
      if (!zoneRooms) return;
      if (zoneId !== selectedZoneId) {
        trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_ZONE_SELECTED, {
          roomCount: zoneRooms.length,
        });
      }
      setSelectedZoneId(zoneId);
      floorPlanPanelHandlers?.current?.zoomToElements({
        elements: zoneRooms.flatMap((room) => room.points),
      });
    },
    [roomsByZoneId, selectedZoneId, floorPlanPanelHandlers],
  );

  const clearZoneSelection = useCallback(() => {
    if (selectedZone === null) return;
    setSelectedZoneId(null);
    floorPlanPanelHandlers?.current?.zoomToFit();
  }, [selectedZone, floorPlanPanelHandlers]);

  const handlePlanClick = useCallback(
    (point: SelectionAreaPoint) => {
      const room = findRoomAtPoint({
        rooms: layout.rooms.filter(isOnActivePage),
        point,
      });
      if (room === null || room.zoneId === null) {
        clearZoneSelection();
        return;
      }
      if (room.zoneId === selectedZone?.id) {
        onEnterRoom(room.id);
        return;
      }
      selectZone(room.zoneId);
    },
    [layout.rooms, selectedZone, selectZone, onEnterRoom, clearZoneSelection],
  );

  useEffect(() => {
    if (!isEnabled) setSelectedZoneId(null);
  }, [isEnabled]);

  useEffect(() => {
    setSelectedZoneId(null);
  }, [resetKey]);

  useEffect(() => {
    if (selectedZoneId !== null && selectedZone === null) {
      setSelectedZoneId(null);
    }
  }, [selectedZoneId, selectedZone]);

  return {
    selectedZone,
    selectedZoneRoomIds,
    selectZone,
    clearZoneSelection,
    handlePlanClick,
  };
};
